import { ReactNode } from 'react';
import { Button, Result } from 'antd';
import { useCrmCapabilities } from '../hooks/useCrmCapabilities';
import { GUARD_COPY } from '../views/Integrations/copy';

/**
 * Stands in front of /integrations. Every company gets the page now — a
 * provider that is off for this company is a greyed card, not a hidden
 * page — so the guard has two jobs left:
 *
 *  - `loading` renders nothing. The cards fail closed to "not enabled" until
 *    the probe answers, and showing them greyed for a beat before flipping
 *    live would tell the tenant two different things.
 *  - `unknown` — the probe failed and has never held an answer — renders an
 *    error with a Retry. Three greyed cards would claim nothing is granted
 *    when all we know is that we couldn't ask. A refetch that fails after an
 *    answer was held stays `ready`; the page keeps the flags it has.
 *
 * `ready` renders the page whatever was granted, including nothing.
 */
export function CrmGuard({ children }: { children: ReactNode }) {
  const { probeStatus, retry } = useCrmCapabilities();
  if (probeStatus === 'loading') return null;
  if (probeStatus === 'unknown') {
    return (
      <Result
        status="warning"
        title={GUARD_COPY.unknownTitle}
        subTitle={GUARD_COPY.unknownBody}
        extra={
          <Button type="primary" onClick={retry}>
            {GUARD_COPY.retryButton}
          </Button>
        }
      />
    );
  }
  return <>{children}</>;
}
